import {BookingOfferPrice} from '../../booking/types/BookingData'
import {toFloatWithTwoDecimals} from '../../utils/number'
import {OfferPrice} from '../../offer/types/offer'
import PriceTypes from '../../offer/types/PriceTypes'

type Price = OfferPrice | BookingOfferPrice

type GetPriceByType = (type: string, prices: Price[]) => Price

export const getPriceByType: GetPriceByType = (type, prices) =>
  prices.find(price => price.type === type)

/**
 * Check whether is there a Currency Conversion
 * A currency conversion is detected when a set of prices contains a DISPLAY_CURRENCY price type
 * @param prices - The list of offer/booking prices
 */
export const isThereACurrencyConversion = (prices: Price[]) => {
  return Boolean(getPriceByType(PriceTypes.DISPLAY_PRICE, prices))
}

/**
Returns the price the user sees. Not necessarily the price in which
he/she was or will be charged
* @param prices - The list of offer/booking prices
 */
export const getDisplayPrice = (prices: Price[]): Price => {
  const type = isThereACurrencyConversion(prices)
    ? PriceTypes.DISPLAY_PRICE
    : PriceTypes.CHARGEABLE_PRICE
  return getPriceByType(type, prices)
}

/**
 Returns the total price including hotel fees regardless of tax display logic
 * @param price - The price object
 */
export const getTotalPrice = (price: Price) => {
  const total = price.chargeable.base + price.chargeable.taxes
  const hotelFees = price?.hotelFees?.total ?? 0

  return toFloatWithTwoDecimals(total + hotelFees)
}

/**
 * to convert price types.
 * This converts the price types we receive in the sapi.rooms() and /bookigns request to the ones we receive in /offer-check
 * The objective of this function is to provide backwards compatibility to the old price types
 * @internal
 * @param price - The list of offer prices
 */
export const convertPricesToNewFormat = (price: Price): Price => {
  const conversionList = [
    {from: PriceTypes.CHARGEABLE_CURRENCY, to: PriceTypes.CHARGEABLE_PRICE},
    {from: PriceTypes.USER_CURRENCY, to: PriceTypes.DISPLAY_PRICE}
  ]

  const copyPrice = {...price}
  conversionList.forEach(mappingItem => {
    if (copyPrice.type === mappingItem.from) {
      // eslint-disable-next-line fp/no-mutation
      copyPrice.type = mappingItem.to
    }
  })
  return copyPrice
}

/**
 Returns the total price excluding hotel fees regardless of tax display logic, unless it is a pay at property deal, then it returns 0
 */
export const getTotalUpFrontPrice = (
  price: Price,
  isPayAtProperty: boolean
) => {
  if (isPayAtProperty) return 0
  return toFloatWithTwoDecimals(price.chargeable.base + price.chargeable.taxes)
}

/**
 * Returns the total price to be paid upfront in cents
 * @param price an offer price object
 */
export const getTotalUpFrontPriceAsCents = (
  price: Price,
  canPayLater: boolean
) => {
  return Math.round(getTotalUpFrontPrice(price, canPayLater) * 100)
}
